(function () {
  const config = window.PNCAThemeCartSettings || {};
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const route = (path) => `${root.replace(/\/?$/, '/')}${path.replace(/^\//, '')}`;
  let cartCache = null;
  let refreshPromise = null;
  let themeCartSyncTimer = null;
  let recommendationTimer = null;
  let expandStickyAfterUpdate = false;
  const recommendationCache = new Map();
  const recommendationInFlight = new Map();

  function formatMoney(cents) {
    const value = Number(cents || 0);
    const amount = value / 100;
    const currency = config.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'JPY';

    if (currency === 'JPY' || String(config.moneyFormat || '').includes('¥')) {
      return `¥${Math.round(amount).toLocaleString('ja-JP')}`;
    }

    try {
      return amount.toLocaleString(undefined, { style: 'currency', currency });
    } catch (error) {
      return amount.toLocaleString();
    }
  }

  function userMessage(error) {
    const message = String(error && error.message ? error.message : '');

    if (message.includes('422') || message.includes('sold out') || message.includes('在庫')) {
      return '在庫数を超えているため追加できませんでした。';
    }

    if (message.includes('variant') || message.includes('購入')) {
      return 'この商品は現在購入できません。';
    }

    return '追加できませんでした。時間をおいて再度お試しください。';
  }

  async function readJson(response) {
    const text = await response.text();

    try {
      return text ? JSON.parse(text) : {};
    } catch (error) {
      throw new Error('JSON parse error');
    }
  }

  async function request(path, options) {
    const response = await fetch(route(path), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      ...options,
    });
    const json = await readJson(response);

    if (!response.ok || json.status || json.errors) {
      const message = json.description || json.message || json.errors || `${response.status}`;
      throw new Error(Array.isArray(message) ? message.join(', ') : String(message));
    }

    return json;
  }

  async function getCart() {
    cartCache = await request('cart.js');
    return cartCache;
  }

  function cartSectionsPayload() {
    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component')]
          .map((element) => element.dataset.sectionId)
          .filter(Boolean)
      ),
    ];

    if (!sectionIds.length) return {};

    return {
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };
  }

  function dispatchCartUpdate(cart, source, extra) {
    const detail = {
      resource: cart,
      sourceId: 'pnca-cart-tools',
      data: {
        source,
        itemCount: cart.item_count,
        ...(extra || {}),
      },
    };

    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('pnca:cart:updated', { bubbles: true, detail: { cart, source, ...(extra || {}) } }));
  }

  function dispatchCartError(error, source) {
    const message = userMessage(error);
    document.dispatchEvent(new CustomEvent('pnca:cart:error', { bubbles: true, detail: { error, message, source } }));
    return message;
  }

  function dispatchGrowthEvent(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  }

  async function refreshCartUI(source) {
    if (!refreshPromise) {
      refreshPromise = getCart()
        .then((cart) => {
          dispatchCartUpdate(cart, source || 'pnca-refresh');
          return cart;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }

    return refreshPromise;
  }

  async function addVariant(variantId, quantity, options) {
    const qty = Math.max(1, Number(quantity || 1));

    if (!variantId) {
      throw new Error('variant id missing');
    }

    try {
      const addResponse = await request('cart/add.js', {
        method: 'POST',
        body: JSON.stringify({
          id: Number(variantId),
          quantity: qty,
          ...cartSectionsPayload(),
        }),
      });
      const cart = await getCart();
      expandStickyAfterUpdate = true;

      document.dispatchEvent(
        new CustomEvent('pnca:cart:item-added', {
          bubbles: true,
          detail: { cart, variantId: String(variantId), quantity: qty },
        })
      );
      dispatchCartUpdate(cart, 'pnca-cart-tools', {
        variantId: String(variantId),
        sections: addResponse.sections || {},
      });

      if (options && options.openDrawer) {
        const drawerDialog = document.querySelector('cart-drawer-component dialog');
        if (drawerDialog && drawerDialog.open) {
          scheduleRecommendationRails(cart, 900, true);
        }
      }

      return cart;
    } catch (error) {
      dispatchCartError(error, 'addVariant');
      throw error;
    }
  }

  async function changeLine(lineOrKey, quantity) {
    try {
      const body = typeof lineOrKey === 'number' || /^\d+$/.test(String(lineOrKey))
        ? { line: Number(lineOrKey), quantity: Number(quantity) }
        : { id: String(lineOrKey), quantity: Number(quantity) };
      const cart = await request('cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ ...body, ...cartSectionsPayload() }),
      });
      dispatchCartUpdate(cart, 'pnca-cart-tools', { sections: cart.sections || {} });
      return cart;
    } catch (error) {
      dispatchCartError(error, 'changeLine');
      throw error;
    }
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');

    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      scheduleRecommendationRails(cartCache, 720, true);
      return;
    }

    if (drawer && typeof drawer.showDialog === 'function') {
      drawer.showDialog();
      scheduleRecommendationRails(cartCache, 720, true);
      return;
    }

    window.location.href = config.cartUrl || route('cart');
  }

  function updateFreeShipping(rootElement, cart) {
    rootElement.querySelectorAll('[data-pnca-free-shipping]').forEach((element) => {
      const threshold = Number(element.dataset.threshold || config.freeShipping?.threshold || 0);
      const magsafeRequired = Number(element.dataset.magsafeRequired || config.freeShipping?.magsafeRequiredQuantity || 2);
      const magsafeProductType = element.dataset.magsafeProductType || config.freeShipping?.magsafeProductType || 'MagSafeトレカケース';
      const magsafeProductIds = new Set(
        String(element.dataset.magsafeProductIds || '')
          .split(',')
          .concat(config.freeShipping?.magsafeProductIds || [])
          .map((id) => String(id).trim())
          .filter(Boolean)
      );
      const magsafeProductHandles = new Set(
        String(element.dataset.magsafeProductHandles || '')
          .split(',')
          .concat(config.freeShipping?.magsafeProductHandles || [])
          .map((handle) => String(handle).trim())
          .filter(Boolean)
      );
      const messageBefore = element.dataset.messageBefore || config.freeShipping?.messageBefore || 'あと{{ amount }}で送料無料';
      const messageMagsafeOne =
        element.dataset.messageMagsafeOne ||
        config.freeShipping?.messageMagsafeOne ||
        'MagSafeトレカケースをあと1点、またはあと{{ amount }}で送料無料';
      const messageAfter = element.dataset.messageAfter || config.freeShipping?.messageAfter || '送料無料になりました';
      const message = element.querySelector('[data-pnca-free-shipping-message]');
      const bar = element.querySelector('[data-pnca-free-shipping-bar]');

      if (!threshold || !config.freeShipping?.enabled) {
        element.hidden = true;
        return;
      }

      const remaining = Math.max(0, threshold - cart.total_price);
      const magsafeCount = (cart.items || []).reduce((sum, item) => {
        const productId = String(item.product_id || item.productId || '');
        const productType = item.product_type || item.productType || item.product?.type || '';
        const title = item.product_title || item.title || '';
        const handle = item.handle || '';
        const isMagsafe =
          (productId && magsafeProductIds.has(productId)) ||
          (handle && magsafeProductHandles.has(handle)) ||
          productType === magsafeProductType ||
          handle.includes('magsafe') ||
          /MagSafe|マグセーフ/.test(title);

        return isMagsafe ? sum + Number(item.quantity || 0) : sum;
      }, 0);
      const subtotalPercent = Math.min(100, Math.round((cart.total_price / threshold) * 100));
      const magsafePercent = magsafeRequired > 0 ? Math.min(100, Math.round((magsafeCount / magsafeRequired) * 100)) : 0;
      const percent = Math.max(subtotalPercent, magsafePercent);
      const isFreeShipping = cart.total_price >= threshold || (magsafeRequired > 0 && magsafeCount >= magsafeRequired);

      element.hidden = false;
      if (element.dataset.pncaViewed !== 'true') {
        element.dataset.pncaViewed = 'true';
        dispatchGrowthEvent('pnca:free_shipping_progress_viewed', {
          threshold,
          magsafeRequired,
          itemCount: cart.item_count,
          totalPrice: cart.total_price,
        });
      }
      if (message) {
        if (isFreeShipping) {
          message.textContent = messageAfter;
        } else if (magsafeCount === 1) {
          message.textContent = messageMagsafeOne.replace('{{ amount }}', formatMoney(remaining));
        } else {
          message.textContent = messageBefore.replace('{{ amount }}', formatMoney(remaining));
        }
      }
      if (bar) {
        bar.style.width = `${percent}%`;
      }
    });
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function lineItemImage(item) {
    const image = item.image || item.featured_image?.url || item.featured_image;
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || '';
  }

  function renderStickyCartItem(item) {
    const image = lineItemImage(item);
    const title = item.product_title || item.title || '商品';
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    const quantity = Number(item.quantity || 0);
    const key = item.key || item.id;
    const linePrice = Number(item.final_line_price || item.line_price || item.final_price || item.price || 0);

    return `
      <article class="pnca-sticky-cart-item" data-pnca-sticky-cart-item data-line-key="${escapeHtml(key)}">
        <div class="pnca-sticky-cart-item__image" aria-hidden="true">
          ${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : ''}
        </div>
        <div class="pnca-sticky-cart-item__body">
          <p class="pnca-sticky-cart-item__title">${escapeHtml(title)}</p>
          ${variantTitle ? `<p class="pnca-sticky-cart-item__variant">${escapeHtml(variantTitle)}</p>` : ''}
          <div class="pnca-sticky-cart-item__meta">
            <span class="pnca-sticky-cart-item__price">${formatMoney(linePrice)}</span>
            <span class="pnca-sticky-cart-item__quantity" aria-label="${escapeHtml(title)}の数量">
              <button type="button" data-pnca-cart-quantity-action="decrease" data-line-key="${escapeHtml(key)}" data-quantity="${quantity}" aria-label="数量を減らす">−</button>
              <span>${quantity}</span>
              <button type="button" data-pnca-cart-quantity-action="increase" data-line-key="${escapeHtml(key)}" data-quantity="${quantity}" aria-label="数量を増やす">＋</button>
            </span>
          </div>
          <button type="button" class="pnca-sticky-cart-item__remove" data-pnca-cart-quantity-action="remove" data-line-key="${escapeHtml(key)}">
            削除
          </button>
        </div>
      </article>
    `;
  }

  function renderStickyCartItems(sticky, cart) {
    const list = sticky.querySelector('[data-pnca-sticky-cart-items]');
    if (!list) return;

    const items = Array.isArray(cart.items) ? cart.items : [];
    list.innerHTML = items.length
      ? items.map(renderStickyCartItem).join('')
      : '<p class="pnca-sticky-cart__empty">カートは空です</p>';
  }

  function setStickyCartExpanded(sticky, expanded) {
    const panel = sticky.querySelector('[data-pnca-sticky-cart-panel]');
    const toggles = sticky.querySelectorAll('[data-pnca-sticky-cart-toggle]');
    if (!panel) return;

    sticky.classList.toggle('is-expanded', expanded);
    panel.hidden = !expanded;
    toggles.forEach((toggle) => {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (toggle.classList.contains('pnca-sticky-cart__button')) {
        toggle.textContent = expanded ? '閉じる' : '中身を見る';
      }
    });
    document.body.classList.toggle('pnca-sticky-cart-expanded', expanded);
  }

  function setStickyCartMessage(sticky, message) {
    const messageElement = sticky.querySelector('[data-pnca-sticky-cart-message]');
    if (messageElement) {
      messageElement.textContent = message || '';
    }
  }

  function updateStickyCart(cart) {
    const sticky = document.querySelector('[data-pnca-sticky-cart]');

    if (!sticky) return;

    const shouldShow = window.matchMedia('(max-width: 749px)').matches && cart.item_count > 0;
    const text = sticky.querySelector('[data-pnca-sticky-cart-text]');

    if (text) {
      text.textContent = `${cart.item_count}点｜${formatMoney(cart.total_price)}`;
    }

    updateFreeShipping(sticky, cart);
    renderStickyCartItems(sticky, cart);
    sticky.hidden = !shouldShow;
    sticky.classList.toggle('is-visible', shouldShow);
    document.body.classList.toggle('pnca-sticky-cart-active', shouldShow);
    if (!shouldShow) {
      setStickyCartExpanded(sticky, false);
    }
  }

  function saleTypeLabel(product) {
    const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',').map((tag) => tag.trim());

    if (tags.includes('sale-type-normal')) return '通常販売';
    if (tags.includes('sale-type-limited')) return '数量限定';
    if (tags.includes('sale-type-preorder')) return '予約販売';
    if (tags.includes('sale-type-made-to-order')) return '受注販売';
    return '';
  }

  function productImage(product) {
    const image = product.featured_image || product.image || (product.images && product.images[0]);

    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || '';
  }

  function firstAvailableVariant(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    return variants.find((variant) => variant.available) || variants[0] || null;
  }

  function productPrice(product, variant) {
    return Number(product.price || variant?.price || 0);
  }

  function renderRecommendationCard(product) {
    const variant = firstAvailableVariant(product);
    const available = product.available !== false && variant && variant.available !== false;
    const image = productImage(product);
    const label = saleTypeLabel(product);
    const url = product.url || (product.handle ? route(`products/${product.handle}`) : '#');
    const title = product.title || '';

    return `
      <article class="pnca-upsell-card" data-product-id="${product.id || ''}">
        <a class="pnca-upsell-card__media" href="${url}">
          ${
            image
              ? `<img class="pnca-upsell-card__image" src="${image}" alt="${title.replace(/"/g, '&quot;')}" loading="lazy">`
              : `<span class="pnca-upsell-card__placeholder">${title.slice(0, 1)}</span>`
          }
        </a>
        <div class="pnca-upsell-card__body">
          <div class="pnca-upsell-card__badges">${label ? `<span class="pnca-product-sale-badge">${label}</span>` : ''}</div>
          <a class="pnca-upsell-card__title" href="${url}">${title}</a>
          <div class="pnca-upsell-card__price"><span class="price">${formatMoney(productPrice(product, variant))}</span></div>
          <button
            type="button"
            class="pnca-upsell-card__button"
            data-pnca-add-variant="${variant ? variant.id : ''}"
            data-pnca-open-cart="true"
            ${available ? '' : 'disabled'}
          >
            <span data-pnca-add-label>${available ? '+追加' : 'SOLD OUT'}</span>
          </button>
          <p class="pnca-upsell-card__message" data-pnca-card-message aria-live="polite"></p>
        </div>
      </article>
    `;
  }

  async function fetchRecommendationPool(productId, limit) {
    const intents = ['complementary', 'related'];
    const poolLimit = Math.min(Math.max(Number(limit || 4) + 6, 8), 10);
    const cacheKey = `${productId}|${poolLimit}`;

    if (recommendationCache.has(cacheKey)) {
      return recommendationCache.get(cacheKey);
    }

    if (recommendationInFlight.has(cacheKey)) {
      return recommendationInFlight.get(cacheKey);
    }

    const fetchPromise = (async () => {
      for (const intent of intents) {
        const url = `recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${poolLimit}&intent=${intent}`;
        try {
          const json = await request(url);
          const products = (json.products || [])
            .filter((product) => product && product.id)
            .filter((product) => product.available !== false && firstAvailableVariant(product));

          if (products.length) {
            recommendationCache.set(cacheKey, products);
            return products;
          }
        } catch (error) {
          // Try the next recommendation intent before falling back to manual products.
        }
      }

      recommendationCache.set(cacheKey, []);
      return [];
    })().finally(() => {
      recommendationInFlight.delete(cacheKey);
    });

    recommendationInFlight.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  async function fetchRecommendations(productId, limit, excludedIds) {
    const products = await fetchRecommendationPool(productId, limit);

    return products
      .filter((product) => product && product.id && !excludedIds.has(String(product.id)))
      .slice(0, limit);
  }

  function fallbackCards(list, excludedIds, limit) {
    return [...list.querySelectorAll('.pnca-upsell-card')]
      .filter((card) => !excludedIds.has(String(card.dataset.productId || '')))
      .slice(0, limit);
  }

  function shouldLoadRecommendationRail(rail, force) {
    if (force) return true;

    const mode = rail.dataset.pncaMode;

    if (mode === 'product-cross-sell') return true;
    if (rail.closest('.cart-page')) return true;

    const dialog = rail.closest('dialog');
    if (dialog) return dialog.hasAttribute('open');

    return true;
  }

  function dispatchRecommendationViewed(rail, productCount) {
    if (rail.dataset.pncaViewed === 'true') return;

    const mode = rail.dataset.pncaMode;
    if (mode !== 'product-cross-sell' && mode !== 'cart-upsell') return;

    rail.dataset.pncaViewed = 'true';
    dispatchGrowthEvent(mode === 'cart-upsell' ? 'pnca:cart_upsell_viewed' : 'pnca:cross_sell_viewed', {
      productId: rail.dataset.productId || '',
      productCount: Number(productCount || 0),
    });
  }

  async function initRecommendationRail(rail, cart, options) {
    const list = rail.querySelector('[data-pnca-recommendation-list]');
    const productId = rail.dataset.productId;
    const limit = Number(rail.dataset.limit || 4);
    const source = rail.dataset.pncaSource || 'empty';
    const section = rail.closest('[data-pnca-recommendation-section]');
    const setHidden = (hidden) => {
      rail.hidden = hidden;
      if (section) section.hidden = hidden;
    };

    if (!shouldLoadRecommendationRail(rail, options?.force)) {
      return;
    }

    if (!list || !productId) {
      setHidden(true);
      return;
    }

    const excludedIds = new Set(String(rail.dataset.excludedProductIds || '').split(',').filter(Boolean));
    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach((item) => excludedIds.add(String(item.product_id)));
    }

    list.querySelectorAll('.pnca-upsell-card').forEach((card) => {
      if (excludedIds.has(String(card.dataset.productId || ''))) {
        card.remove();
      }
    });

    if (source === 'metafield') {
      const cardCount = list.querySelectorAll('.pnca-upsell-card').length;
      setHidden(cardCount === 0);
      if (cardCount > 0) {
        dispatchRecommendationViewed(rail, cardCount);
      }
      return;
    }

    const cacheKey = `${productId}|${[...excludedIds].sort().join('-')}|${limit}`;
    if (rail.dataset.pncaLoadedFor === cacheKey) {
      const cardCount = list.querySelectorAll('.pnca-upsell-card').length;
      setHidden(cardCount === 0);
      if (cardCount > 0) {
        dispatchRecommendationViewed(rail, cardCount);
      }
      return;
    }

    rail.dataset.pncaLoadedFor = cacheKey;

    try {
      const products = await fetchRecommendations(productId, limit, excludedIds);
      if (products.length) {
        list.innerHTML = products.map(renderRecommendationCard).join('');
        setHidden(false);
        dispatchRecommendationViewed(rail, products.length);
        return;
      }
    } catch (error) {
      // Fallback Liquid cards remain visible when recommendations are unavailable.
    }

    const visibleFallbackCards = fallbackCards(list, excludedIds, limit);
    if (visibleFallbackCards.length) {
      list.innerHTML = '';
      visibleFallbackCards.forEach((card) => list.appendChild(card));
      setHidden(false);
      dispatchRecommendationViewed(rail, visibleFallbackCards.length);
    } else {
      setHidden(true);
    }
  }

  async function initRecommendationRails(cart, options) {
    const currentCart = cart || cartCache || (await getCart().catch(() => null));
    const rails = document.querySelectorAll('[data-pnca-recommendations]');

    rails.forEach((rail) => {
      initRecommendationRail(rail, currentCart, options);
    });
  }

  function scheduleRecommendationRails(cart, delay, force) {
    if (recommendationTimer) {
      window.clearTimeout(recommendationTimer);
    }

    recommendationTimer = window.setTimeout(() => {
      recommendationTimer = null;
      initRecommendationRails(cart || cartCache, { force }).catch(() => {});
    }, typeof delay === 'number' ? delay : 450);
  }

  function initStickyCartButton() {
    document.addEventListener('click', async (event) => {
      const toggleButton = event.target.closest('[data-pnca-sticky-cart-toggle]');
      if (toggleButton) {
        const sticky = toggleButton.closest('[data-pnca-sticky-cart]');
        if (!sticky) return;

        const shouldExpand = !sticky.classList.contains('is-expanded');
        dispatchGrowthEvent('pnca:sticky_cart_clicked', {
          action: shouldExpand ? 'open_mini_cart' : 'close_mini_cart',
          itemCount: cartCache?.item_count || 0,
          totalPrice: cartCache?.total_price || 0,
        });
        setStickyCartExpanded(sticky, shouldExpand);
        setStickyCartMessage(sticky, '');
        return;
      }

      const quantityButton = event.target.closest('[data-pnca-cart-quantity-action]');
      if (quantityButton && !quantityButton.disabled) {
        const sticky = quantityButton.closest('[data-pnca-sticky-cart]');
        const item = quantityButton.closest('[data-pnca-sticky-cart-item]');
        const action = quantityButton.dataset.pncaCartQuantityAction;
        const lineKey = quantityButton.dataset.lineKey || item?.dataset.lineKey;
        const currentQuantity = Number(quantityButton.dataset.quantity || item?.querySelector('[data-quantity]')?.dataset.quantity || 1);
        let nextQuantity = currentQuantity;

        if (!sticky || !lineKey) return;

        if (action === 'increase') {
          nextQuantity = currentQuantity + 1;
        } else if (action === 'decrease') {
          nextQuantity = Math.max(0, currentQuantity - 1);
        } else if (action === 'remove') {
          nextQuantity = 0;
        }

        item?.querySelectorAll('button').forEach((button) => {
          button.disabled = true;
        });
        setStickyCartMessage(sticky, 'カートを更新中です');

        try {
          const cart = await changeLine(lineKey, nextQuantity);
          const currentSticky = document.querySelector('[data-pnca-sticky-cart]');
          if (currentSticky && cart.item_count > 0) {
            setStickyCartExpanded(currentSticky, true);
            setStickyCartMessage(currentSticky, 'カートを更新しました');
          }
        } catch (error) {
          item?.querySelectorAll('button').forEach((button) => {
            button.disabled = false;
          });
          setStickyCartMessage(sticky, userMessage(error));
        }
        return;
      }

      const button = event.target.closest('[data-pnca-sticky-cart-cta]');
      if (!button) return;

      const sticky = button.closest('[data-pnca-sticky-cart]');
      const ctaType = button.dataset.ctaType || sticky?.dataset.ctaType || 'cart';

      if (ctaType === 'checkout') {
        dispatchGrowthEvent('pnca:sticky_cart_clicked', {
          action: 'checkout',
          itemCount: cartCache?.item_count || 0,
          totalPrice: cartCache?.total_price || 0,
        });
        window.location.href = config.checkoutUrl || route('checkout');
      } else {
        dispatchGrowthEvent('pnca:sticky_cart_clicked', {
          action: 'cart_page',
          itemCount: cartCache?.item_count || 0,
          totalPrice: cartCache?.total_price || 0,
        });
        window.location.href = config.cartUrl || route('cart');
      }
    });
  }

  function initAddButtons() {
    document.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-pnca-add-variant]');
      if (!button || button.disabled) return;

      const label = button.querySelector('[data-pnca-add-label]');
      const message = button.closest('.pnca-upsell-card')?.querySelector('[data-pnca-card-message]');
      const rail = button.closest('[data-pnca-recommendations]');
      const mode = rail?.dataset.pncaMode || '';
      const originalLabel = label ? label.textContent : button.textContent;

      button.disabled = true;
      button.classList.add('is-loading');
      if (label) label.textContent = '追加中';
      if (message) message.textContent = '';

      try {
        await addVariant(button.dataset.pncaAddVariant, 1, { openDrawer: button.dataset.pncaOpenCart === 'true' });
        if (mode === 'cart-upsell' || mode === 'product-cross-sell') {
          dispatchGrowthEvent(mode === 'cart-upsell' ? 'pnca:cart_upsell_added' : 'pnca:cross_sell_added', {
            variantId: String(button.dataset.pncaAddVariant || ''),
            productId: button.closest('.pnca-upsell-card')?.dataset.productId || '',
          });
        }
        if (label) label.textContent = '追加済み';
        if (message) message.textContent = 'カートに追加しました';
      } catch (error) {
        if (label) label.textContent = originalLabel;
        if (message) message.textContent = userMessage(error);
      } finally {
        window.setTimeout(() => {
          button.disabled = false;
          button.classList.remove('is-loading');
          if (label) label.textContent = originalLabel;
        }, 900);
      }
    });
  }

  function listenForThemeCartUpdates() {
    document.addEventListener('cart:update', (event) => {
      if (event.detail?.data?.source === 'pnca-cart-tools') return;

      if (event.detail?.data?.source === 'product-form-component' && !event.detail?.data?.didError) {
        expandStickyAfterUpdate = true;
      }

      if (themeCartSyncTimer) {
        window.clearTimeout(themeCartSyncTimer);
      }

      themeCartSyncTimer = window.setTimeout(() => {
        themeCartSyncTimer = null;
        getCart()
          .then((cart) => {
            document.dispatchEvent(new CustomEvent('pnca:cart:updated', { bubbles: true, detail: { cart, source: 'theme-cart-update' } }));
          })
          .catch(() => {});
      }, 220);
    });

    document.addEventListener('pnca:cart:updated', (event) => {
      const cart = event.detail?.cart;
      if (!cart) return;

      cartCache = cart;
      updateStickyCart(cart);
      updateFreeShipping(document, cart);
      if (expandStickyAfterUpdate) {
        if (cart.item_count > 0) {
          const sticky = document.querySelector('[data-pnca-sticky-cart]');
          if (sticky && window.matchMedia('(max-width: 749px)').matches) {
            setStickyCartExpanded(sticky, true);
            setStickyCartMessage(sticky, 'カートに追加しました');
          }
        }
        expandStickyAfterUpdate = false;
      }
      scheduleRecommendationRails(cart, 900, false);
    });
  }

  function initCartDrawerRecommendationLoading() {
    document.addEventListener('click', (event) => {
      const cartTrigger = event.target.closest('cart-drawer-component button, [data-pnca-sticky-cart-cta]');
      if (!cartTrigger) return;

      scheduleRecommendationRails(cartCache, 720, true);
    });

    document.querySelectorAll('cart-drawer-component dialog').forEach((dialog) => {
      const observer = new MutationObserver(() => {
        if (dialog.hasAttribute('open')) {
          scheduleRecommendationRails(cartCache, 900, true);
        }
      });

      observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    });
  }

  function init() {
    window.PNCAThemeCart = {
      getCart,
      addVariant,
      changeLine,
      refreshCartUI,
      openCartDrawer,
      formatMoney,
    };

    initStickyCartButton();
    initAddButtons();
    initCartDrawerRecommendationLoading();
    listenForThemeCartUpdates();

    getCart()
      .then((cart) => {
        cartCache = cart;
        updateStickyCart(cart);
        updateFreeShipping(document, cart);
        scheduleRecommendationRails(cart, 700, false);
      })
      .catch(() => {
        updateStickyCart({ item_count: 0, total_price: 0, items: [] });
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
